import { redirect } from 'react-router-dom'
import type { ABTestRouteConfig } from '../types/abtest'

/**
 * Get the route configuration (ordered pages) for a specific variant
 */
export function getRouteConfigForVariant(
  variant: string,
  config: ABTestRouteConfig,
): string[] {
  if (variant === 'baseline') {
    return config.baseline
  }

  const variantRoutes = config.variants[variant]
  if (!variantRoutes) {
    console.warn(
      `Variant "${variant}" not found in route config, using baseline`,
    )
    return config.baseline
  }

  return variantRoutes
}

/**
 * Get the initial redirect path for a given variant
 * Returns just the first page name (e.g., 'question-language')
 */
export function getInitialRedirectPath(
  variant: string,
  config: ABTestRouteConfig,
): string {
  const routes = getRouteConfigForVariant(variant, config)
  const firstRoute = routes[0]

  if (!firstRoute) {
    console.warn('No routes found for variant, using fallback')
    return '/'
  }

  return firstRoute
}

/**
 * Navigate to the next onboarding page based on the current page and variant
 * All variants use the same URLs - variant is determined by context
 */
export async function navigateToNextOnboardingPage({
  request,
  routeConfig,
  variant,
  redirectOnComplete,
}: {
  request: Request
  routeConfig: ABTestRouteConfig
  variant: string
  redirectOnComplete: string
}): Promise<Response> {
  const url = new URL(request.url)
  const currentPage = url.pathname.split('/').pop() || ''

  // Get ordered routes for this variant
  const orderedRoutes = getRouteConfigForVariant(variant, routeConfig)

  // Find current page in the order
  const currentIndex = orderedRoutes.indexOf(currentPage)

  if (currentIndex === -1) {
    console.warn(
      `Current page "${currentPage}" not found in variant "${variant}" routes`,
    )
    return redirect(redirectOnComplete)
  }

  // Get next page
  const nextPage = orderedRoutes[currentIndex + 1]

  if (!nextPage) {
    // Completed onboarding
    return redirect(redirectOnComplete)
  }

  // Build next path (just the page name, no variant prefix)
  const basePath = url.pathname.split('/').slice(0, -1).join('/')
  const nextPath = `${basePath}/${nextPage}`

  return redirect(nextPath)
}
